<script setup>
import { useDialogPluginComponent } from 'quasar';
import env from 'src/configs/env.js';
import { useAppStore } from 'stores/app-store.js';
import { onMounted, onUnmounted, ref, defineProps } from 'vue';
import DialogLogoHeaderSection from 'components/DialogLogoHeaderSection.vue';

const $props = defineProps({
    type: {
        type: String,
        default: 'client'
    }
});

const $emit = defineEmits([...useDialogPluginComponent.emits]);

const { dialogRef: dialogElement, onDialogHide } = useDialogPluginComponent();

const appStore = useAppStore();

let statusCheckTimeout = null;
let initialWaitTimeout = null;
const cooldownIsActive = ref(false);
const initialWaitActive = ref(true);

onMounted(() => {
    scheduleNextCheck();
    initialWaitTimeout = setTimeout(() => {
        initialWaitActive.value = false;
    }, 10000);
});

onUnmounted(() => {
    clearCheckTimeout();
});

function statusCheckUrl() {
    return $props.type === 'server'
        ? env.apiServerEndpoint + '/status'
        : '/status';
}

function scheduleNextCheck() {
    statusCheckTimeout = setTimeout(checkServiceStatus, 60000);
}

function clearCheckTimeout() {
    if (statusCheckTimeout) {
        clearTimeout(statusCheckTimeout);
        statusCheckTimeout = null;
    }
}

function retryService() {
    if (cooldownIsActive.value) {
        return;
    }
    cooldownIsActive.value = true;
    checkServiceStatus(true);
    setTimeout(() => {
        cooldownIsActive.value = false;
    }, 10000);
}

function checkServiceStatus(isRetry = false) {
    fetch(statusCheckUrl()).then(response => {
        if (response.ok) {
            $props.type === 'server'
                ? appStore.unavailableServiceDialog = null
                : window.location.reload();
        } else {
            if (isRetry) {
                scheduleNextCheck();
            }
            console.log('Service is still unavailable, will check again in 60 seconds.');
        }
    }).catch(err => {
        console.error('Error checking service status:', err);
        scheduleNextCheck();
    });
}
</script>

<template>
    <q-dialog ref="dialogElement" @hide="onDialogHide" class="unavailable-service-dialog" persistent>
        <q-card class="card">
            <q-card-section>
                <DialogLogoHeaderSection hide-close-btn/>
            </q-card-section>
            <q-card-section class="text-center q-pt-none">
                <h5 class="q-mt-md">Service is temporarily unavailable</h5>
                <p>
                    We're likely deploying updates to improve your experience, or there may be a brief service interruption.
                </p>
                <p class="q-mb-none">
                    No need to refresh the page &mdash; we'll check automatically every 60 seconds and reconnect when the service is back online, but if you'd like to check sooner,
                    just click "Check again" below.
                </p>
            </q-card-section>
            <q-card-actions align="center">
                <q-btn @click="retryService" label="Check again" aria-label="Check again" :disabled="cooldownIsActive || initialWaitActive" color="primary"/>
            </q-card-actions>
        </q-card>
    </q-dialog>
</template>

<style lang="scss" scoped>
.q-dialog__inner:not(.q-dialog__inner--maximized) .card {
    max-width: 550px;
    width: 100%;
}
</style>
